import { useEffect, useRef, useCallback } from 'react'

interface UseSmartPollingOptions {
  onPoll: () => Promise<any>
  baseInterval?: number
  maxInterval?: number
  activeInterval?: number
  enabled?: boolean
}

export const useSmartPolling = ({
  onPoll,
  baseInterval = 10000,
  maxInterval = 30000,
  activeInterval = 5000,
  enabled = true
}: UseSmartPollingOptions) => {
  const pollingIntervalRef = useRef<NodeJS.Timeout>()
  const lastActivityRef = useRef<number>(Date.now())
  const userIsActiveRef = useRef(false)
  const activeTimeoutRef = useRef<NodeJS.Timeout>()

  const getCurrentPollingInterval = useCallback(() => {
    if (userIsActiveRef.current) {
      return activeInterval
    }

    const timeSinceActivity = Date.now() - lastActivityRef.current
    const minutesSinceLastActivity = timeSinceActivity / 60000

    if (minutesSinceLastActivity < 2) {
      return baseInterval
    }

    if (minutesSinceLastActivity < 5) {
      return baseInterval * 2
    }

    if (minutesSinceLastActivity < 10) {
      return baseInterval * 3
    }

    return maxInterval
  }, [activeInterval, baseInterval, maxInterval])

  const resetPollingInterval = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
    }

    if (!enabled) {
      return
    }

    const newIntervalBasedOnTimeSinceActivity = getCurrentPollingInterval()
    pollingIntervalRef.current = setInterval(() => {
      onPoll()
    }, newIntervalBasedOnTimeSinceActivity)
  }, [enabled, getCurrentPollingInterval, onPoll])

  const markActivity = useCallback(() => {
    lastActivityRef.current = Date.now()
    userIsActiveRef.current = true
    resetPollingInterval()

    const resetActiveTimeoutUponEachActivity = () => {
      if (activeTimeoutRef.current) {
        clearTimeout(activeTimeoutRef.current)
      }
    }

    resetActiveTimeoutUponEachActivity()

    const setTimeoutToGetBackToInactiveMode = () => {
      activeTimeoutRef.current = setTimeout(() => {
        userIsActiveRef.current = false
        resetPollingInterval()
      }, 3000)
    }

    setTimeoutToGetBackToInactiveMode()
  }, [resetPollingInterval])

  useEffect(() => {
    if (!enabled) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }
      return
    }

    onPoll()
    resetPollingInterval()

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        onPoll()
        resetPollingInterval()
        return
      }

      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }
    }

    const handleActivity = () => {
      markActivity()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    document.addEventListener('keydown', handleActivity)
    document.addEventListener('click', handleActivity)

    const intervalToConstantlyResetPollingInterval = setInterval(() => {
      resetPollingInterval()
    }, 60000)

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }

      if (activeTimeoutRef.current) {
        clearTimeout(activeTimeoutRef.current)
      }

      clearInterval(intervalToConstantlyResetPollingInterval)

      document.removeEventListener('visibilitychange', handleVisibilityChange)
      document.removeEventListener('keydown', handleActivity)
      document.removeEventListener('click', handleActivity)
    }
  }, [enabled, onPoll, resetPollingInterval, markActivity])

  return { markActivity } // not used out of the box but you can get this from the custom hook to reset interval actively if needed
}
